import React from 'react';
import { Column, Row, SortByFn, useSortBy, useTable } from 'react-table';
import { File } from '@models/files';
import fileSize from 'filesize';
import { useSelector } from 'react-redux';
import { IApplicationState } from '@store/reducer';
import { formatDateWithTimezone } from '@utils/dateTime';
import { SingleFile } from './SingleFile';
import { Folder } from './Folder';
import { useTranslation } from 'react-i18next';

interface IFilesTable {
  data: File[];
  relativePath: string;
}
export const FilesTable: React.FC<IFilesTable> = ({ data, relativePath }) => {
  const { timeZone } = useSelector((state: IApplicationState) => ({
    timeZone: state.projectPicker.timeZone,
  }));
  const { t } = useTranslation();

  const sortWithinType: SortByFn<File> = (
    rowA: Row<File>,
    rowB: Row<File>,
    accessor: string,
    desc: boolean | undefined
  ) => {
    let valA: File = rowA.original,
      valB: File = rowB.original;
    if (valA.isFolder && valB.isFolder) {
      return valA.name.localeCompare(valB.name);
    } else if (!valA.isFolder && !valB.isFolder) {
      if (typeof rowA.values[accessor] === 'string') {
        return rowA.values[accessor].localeCompare(rowB.values[accessor]);
      } else {
        return rowA.values[accessor] > rowB.values[accessor] ? 1 : -1;
      }
    } else {
      // folders are first, then files, regardless of sort order
      return valB.isFolder && !desc ? 1 : -1;
    }
  };
  const sortTypes: Record<string, SortByFn<File>> = {
    sortWithinType: sortWithinType,
  };

  const getColumns = (): Column<File>[] => {
    return [
      {
        Header: t('files_header_name').toString(),
        accessor: 'name',
        sortType: 'sortWithinType',
        Cell: ({ row }) => {
          if (row.original.isFolder) {
            return (
              <Folder currentFile={row.original} relativePath={relativePath} />
            );
          } else {
            return (
              <SingleFile
                currentFile={row.original}
                relativePath={relativePath}
              />
            );
          }
        },
      },
      {
        Header: t('files_header_uploadDate').toString(),
        accessor: 'uploadDate',
        sortType: 'sortWithinType',
        Cell: ({ row }) => {
          if (!row.original.isFolder) {
            return formatDateWithTimezone(row.original.uploadDate, timeZone);
          } else return null;
        },
      },
      {
        Header: t('files_header_contentType').toString(),
        accessor: 'contentType',
        sortType: 'sortWithinType',
        Cell: ({ row }) => {
          if (!row.original.isFolder) {
            return row.original.contentType;
          } else return 'Folder';
        },
      },
      {
        Header: t('files_header_size').toString(),
        accessor: 'contentLength',
        sortType: 'sortWithinType',
        Cell: ({ value }) => {
          if (value > 0) {
            return fileSize(value, { round: 1, base: 2, standard: 'jedec' });
          } else return null;
        },
      },
    ];
  };
  const columns = React.useMemo(getColumns, [timeZone]);
  const {
    getTableProps, // table props from react-table
    getTableBodyProps, // table body props from react-table
    headerGroups, // headerGroups, if your table has groupings
    rows, // rows for the table based on the data passed
    prepareRow, // Prepare the row (this function needs to be called for each row before getting the row props)
  } = useTable(
    {
      columns,
      data,
      disableSortRemove: true,
      sortTypes: sortTypes,
      initialState: {
        sortBy: React.useMemo(() => [{ id: 'name', desc: false }], []),
      },
    },
    useSortBy
  );

  return (
    <table {...getTableProps()} className='standard-table files-table'>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                {column.render('Header')}
                <span
                  key={`${column.isSortedDesc}`}
                  className='ml-1 fcolor--primary-brand d-inline-block'
                  style={{ width: 8 }}
                >
                  {column.isSorted ? (
                    column.isSortedDesc ? (
                      <i className='fas fa-long-arrow-alt-down'></i>
                    ) : (
                      <i className='fas fa-long-arrow-alt-up'></i>
                    )
                  ) : (
                    <React.Fragment />
                  )}
                </span>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell) => {
                return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
